import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/apiConfig';
import { XMarkIcon, ExclamationCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface Category {
  id: number;
  name: string;
}

interface CategoryFormProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoryCreated?: (category: Category) => void;
  onCategoryUpdated?: (category: Category) => void;
  initialCategory?: Category | null;
  isEditMode?: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  isOpen,
  onClose,
  onCategoryCreated,
  onCategoryUpdated,
  initialCategory = null,
  isEditMode = false,
}) => {
  const [categoryName, setCategoryName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialCategory) {
        setCategoryName(initialCategory.name);
      } else {
        setCategoryName('');
      }
      setSubmitError(null);
      setSubmitSuccess(false);
    }
  }, [isOpen, isEditMode, initialCategory]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      setSubmitError('Category name is required');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let response;

      if (isEditMode && initialCategory) {
        // Update existing category
        response = await apiClient.put(`/api/categories/${initialCategory.id}/`, {
          name: categoryName.trim(),
        });

        if (response.status >= 200 && response.status < 300) {
          setSubmitSuccess(true);
          onCategoryUpdated?.(response.data);

          setTimeout(() => {
            onClose();
            setSubmitSuccess(false);
          }, 1000);
        } else {
          throw new Error(`Failed to update category (${response.status})`);
        }
      } else {
        // Create new category
        response = await apiClient.post('/api/categories/', {
          name: categoryName.trim(),
        });

        if (response.status >= 200 && response.status < 300) {
          setSubmitSuccess(true);
          onCategoryCreated?.(response.data);

          setTimeout(() => {
            onClose();
            setSubmitSuccess(false);
          }, 1000);
        } else {
          throw new Error(`Failed to create category (${response.status})`);
        }
      }
    } catch (error) {
      console.error('Error submitting category:', error);

      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 50,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          minHeight: '100vh',
          paddingTop: SPACING[16],
          paddingLeft: SPACING[16],
          paddingRight: SPACING[16],
          paddingBottom: '80px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: 'rgba(107, 114, 128, 0.75)',
            transition: 'opacity 150ms ease-in-out',
          }}
          onClick={handleClose}
        />

        <span
          style={{
            display: 'inline-block',
            verticalAlign: 'middle',
            height: '100vh',
          }}
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div
          style={{
            display: 'inline-block',
            verticalAlign: 'middle',
            backgroundColor: COLORS.bg.panel,
            borderRadius: RADIUS,
            paddingLeft: SPACING[24],
            paddingRight: SPACING[24],
            paddingTop: SPACING[24],
            paddingBottom: SPACING[24],
            textAlign: 'left',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            transform: 'translateY(0)',
            transition: 'all 150ms ease-in-out',
            maxWidth: '512px',
            width: '100%',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              paddingTop: SPACING[16],
              paddingRight: SPACING[16],
            }}
          >
            <button
              type="button"
              style={{
                backgroundColor: COLORS.bg.panel,
                borderRadius: RADIUS,
                color: COLORS.text.tertiary,
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                padding: SPACING[4],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <span
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  padding: 0,
                  margin: '-1px',
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  borderWidth: 0,
                }}
              >
                Close
              </span>
              <XMarkIcon style={{ height: '24px', width: '24px' }} />
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                marginTop: 0,
                textAlign: 'left',
                width: '100%',
              }}
            >
              <h3
                style={{
                  fontSize: TYPOGRAPHY.headings.sizes.h2,
                  lineHeight: '1.5',
                  fontWeight: TYPOGRAPHY.weight.medium,
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  color: COLORS.text.primary,
                  marginBottom: SPACING[16],
                }}
              >
                {isEditMode ? 'Edit Category' : 'Add New Category'}
              </h3>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: SPACING[16] }}>
                  <label
                    htmlFor="category-name"
                    style={{
                      display: 'block',
                      fontSize: TYPOGRAPHY.size.sm,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      color: COLORS.text.secondary,
                    }}
                  >
                    Category Name
                  </label>
                  <div style={{ marginTop: SPACING[4] }}>
                    <input
                      type="text"
                      id="category-name"
                      value={categoryName}
                      onChange={(e) => setCategoryName(e.target.value)}
                      style={{
                        display: 'block',
                        width: '100%',
                        borderRadius: RADIUS,
                        border: `1px solid ${COLORS.border.default}`,
                        padding: `${SPACING[8]} ${SPACING[12]}`,
                        fontSize: TYPOGRAPHY.size.sm,
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        color: COLORS.text.primary,
                        backgroundColor: COLORS.bg.panel,
                        outline: 'none',
                        boxSizing: 'border-box',
                        opacity: isSubmitting ? 0.5 : 1,
                      }}
                      placeholder="Enter category name"
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                </div>

                {submitError && (
                  <div
                    style={{
                      borderRadius: RADIUS,
                      backgroundColor: '#FEF2F2',
                      padding: SPACING[16],
                      marginBottom: SPACING[16],
                    }}
                  >
                    <div style={{ display: 'flex' }}>
                      <ExclamationCircleIcon
                        style={{
                          height: '20px',
                          width: '20px',
                          color: COLORS.status.error,
                        }}
                      />
                      <div style={{ marginLeft: SPACING[12] }}>
                        <p
                          style={{
                            fontSize: TYPOGRAPHY.size.sm,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            color: '#B91C1C',
                            margin: 0,
                          }}
                        >
                          {submitError}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {submitSuccess && (
                  <div
                    style={{
                      borderRadius: RADIUS,
                      backgroundColor: '#ECFDF5',
                      padding: SPACING[16],
                      marginBottom: SPACING[16],
                    }}
                  >
                    <div style={{ display: 'flex' }}>
                      <div style={{ marginLeft: SPACING[12] }}>
                        <p
                          style={{
                            fontSize: TYPOGRAPHY.size.sm,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            color: '#047857',
                            margin: 0,
                          }}
                        >
                          Category {isEditMode ? 'updated' : 'created'} successfully!
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  style={{
                    marginTop: SPACING[16],
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    gap: SPACING[12],
                  }}
                >
                  <button
                    type="submit"
                    disabled={isSubmitting || !categoryName.trim()}
                    style={{
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: RADIUS,
                      border: 'none',
                      padding: `${SPACING[8]} ${SPACING[16]}`,
                      backgroundColor: COLORS.accent.default,
                      fontSize: TYPOGRAPHY.size.sm,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      color: '#FFFFFF',
                      cursor: isSubmitting || !categoryName.trim() ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting || !categoryName.trim() ? 0.5 : 1,
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <ArrowPathIcon
                          style={{
                            width: '16px',
                            height: '16px',
                            marginRight: SPACING[8],
                            animation: 'spin 1s linear infinite',
                          }}
                        />
                        {isEditMode ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      isEditMode ? 'Update Category' : 'Create Category'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    style={{
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: RADIUS,
                      border: `1px solid ${COLORS.border.default}`,
                      padding: `${SPACING[8]} ${SPACING[16]}`,
                      backgroundColor: COLORS.bg.panel,
                      fontSize: TYPOGRAPHY.size.sm,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      color: COLORS.text.secondary,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting ? 0.5 : 1,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryForm;
